import React, { useEffect, useState } from 'react';
import { Award, CheckCircle, XCircle, ArrowLeft, MessageCircle, Send, XIcon, ChevronDown, ChevronRight, InfoIcon, Filter, PlayIcon, RefreshCcw, AlertTriangle, Target, TrendingUp, Clock } from 'lucide-react';
import { motion, AnimatePresence, useSpring, useTransform } from 'framer-motion';
export function FeedbackPage({
  assessmentResults,
  questions,
  userAnswers,
  confidenceLevels,
  onBackToAssessment
}) {
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showRetakeModal, setShowRetakeModal] = useState(false);
  const [messages, setMessages] = useState([{
    type: 'bot',
    content: 'Hi there! How can I help you understand your assessment results?',
    timestamp: new Date()
  }]);
  const [newMessage, setNewMessage] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  // Animated counter for score
  const [displayScore, setDisplayScore] = useState(0);
  // Ensure questions 1 and 7 have confidence levels
  const enhancedConfidenceLevels = {
    ...confidenceLevels,
    0: confidenceLevels[0] || 'unsure',
    6: confidenceLevels[6] || 'sure' // Set question 7 to 'sure' if not already set
  };
  // Process results to accurately reflect incorrect questions
  const processedResults = assessmentResults.map((result, index) => {
    // Force questions 2, 4, 7, 10, and 11 to be incorrect for demonstration
    if ([1, 3, 6, 9, 10].includes(index)) {
      return {
        ...result,
        isCorrect: false,
        feedback: index === 1 ? 'Remember that stakeholder changes need to be evaluated for their impact on project constraints before proceeding.' : index === 3 ? 'Consider facilitating a structured discussion to understand all perspectives before making decisions.' : index === 6 ? 'This answer is incorrect. Remember to analyze the critical path before making decisions.' : index === 9 ? 'Your approach could be improved by focusing more on critical path analysis and resource allocation.' : 'Review the project closure activities that contribute to organizational learning.'
      };
    }
    // For all other questions, mark them as correct
    return {
      ...result,
      isCorrect: true
    };
  });
  // Calculate overall score based on processed results
  const totalQuestions = questions.length;
  // Override with 5 incorrect answers
  const incorrectAnswers = 5; // Show exactly 5 incorrect for demonstration
  // Adjust the correctAnswers to maintain the total questions count
  const adjustedCorrectAnswers = totalQuestions - incorrectAnswers;
  const scorePercentage = Math.round(adjustedCorrectAnswers / totalQuestions * 100);
  // Define passing threshold
  const passingThreshold = 85;
  const hasPassed = scorePercentage >= passingThreshold;
  // Get incorrect question indexes - Questions 2, 4, 7, 10, and 11
  const incorrectQuestionIndexes = [1, 3, 6, 9, 10]; // indexes for incorrect questions (0-indexed)
  // Calculate average confidence
  const confidenceValues = Object.values(confidenceLevels).filter(Boolean);
  const sureCount = confidenceValues.filter(c => c === 'sure').length;
  const confidenceAverage = confidenceValues.length > 0 ? Math.round(sureCount / confidenceValues.length * 100) : 0;
  // Animated counter effect
  useEffect(() => {
    let start = 0;
    const end = scorePercentage;
    const duration = 2000;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setDisplayScore(end);
        clearInterval(timer);
      } else {
        setDisplayScore(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [scorePercentage]);
  // Filter questions based on the active filter
  const filteredResults = processedResults.filter((result, index) => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'correct') return result.isCorrect;
    if (activeFilter === 'incorrect') return !result.isCorrect;
    return true;
  });
  const handleBackToAssessment = () => {
    if (onBackToAssessment) {
      onBackToAssessment();
    }
  };
  const handleRetakeAssessment = (focusOnMissed = false) => {
    if (onBackToAssessment) {
      onBackToAssessment(focusOnMissed ? incorrectQuestionIndexes : null);
    }
    setShowRetakeModal(false);
  };
  const sendMessage = () => {
    if (newMessage.trim() === '') return;
    // Add user message
    const userMessage = {
      type: 'user',
      content: newMessage,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    // Simulate bot response after a delay
    setTimeout(() => {
      const botResponse = {
        type: 'bot',
        content: "I see you're reviewing your assessment results. Is there a specific question you'd like me to explain in more detail?",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botResponse]);
    }, 1000);
  };
  const toggleQuestionDetails = questionId => {
    if (activeQuestionId === questionId) {
      setActiveQuestionId(null);
    } else {
      setActiveQuestionId(questionId);
    }
  };
  return <div className="flex-1 p-4 md:p-8 relative" data-id="element-210">
      <div className="max-w-[1000px] mx-auto" data-id="element-211">
        {/* Header with Back Button */}
        <div className="mb-8" data-id="element-212">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8" data-id="element-213">
            {/* New Split-Screen Hero Layout */}
            <div className="min-h-[320px] rounded-2xl overflow-hidden shadow-2xl mb-6" data-id="element-214">
              <div className="flex flex-col md:flex-row h-full" data-id="element-215">
                {/* Left Side - 40% - Score Display */}
                <div className="w-full md:w-[40%] bg-[#00638C] p-8 flex flex-col justify-center items-center text-white" data-id="element-216">
                  <motion.div initial={{
                  scale: 0.5,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} transition={{
                  duration: 0.5,
                  type: 'spring'
                }} className="text-center" data-id="element-217">
                    <div className="text-7xl font-bold mb-2" data-id="element-218">
                      {displayScore}%
                    </div>
                    <div className="text-sm uppercase tracking-wide opacity-90" data-id="element-219">
                      Your Score
                    </div>
                  </motion.div>
                </div>
                {/* Right Side - 60% - Status & Details */}
                <div className="w-full md:w-[60%] bg-white p-8 flex flex-col justify-between border border-gray-100" data-id="element-220">
                  {/* Pass/Fail Status */}
                  <div className="mb-6" data-id="element-221">
                    <div className="flex items-center gap-3 mb-4" data-id="element-222">
                      {hasPassed ? <>
                          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center" data-id="element-223">
                            <CheckCircle size={24} className="text-green-600" data-id="element-224" />
                          </div>
                          <div data-id="element-225">
                            <h3 className="text-2xl font-bold text-gray-900" data-id="element-226">
                              Passed!
                            </h3>
                            <p className="text-sm text-gray-500" data-id="element-227">
                              Congratulations on your achievement
                            </p>
                          </div>
                        </> : <>
                          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-228">
                            <XCircle size={24} className="text-red-600" data-id="element-229" />
                          </div>
                          <div data-id="element-230">
                            <h3 className="text-2xl font-bold text-gray-900" data-id="element-231">
                              Not Passed
                            </h3>
                            <p className="text-sm text-gray-500" data-id="element-232">
                              Keep learning and try again
                            </p>
                          </div>
                        </>}
                    </div>
                    {/* Status Message */}
                    <p className="text-base text-gray-700 leading-relaxed" data-id="element-233">
                      {hasPassed ? "Excellent work! You've demonstrated strong understanding of the material and met the passing criteria. Continue building on this foundation." : `You need to score at least ${passingThreshold}% to pass this assessment. Review the feedback below and focus on the areas that need improvement.`}
                    </p>
                  </div>
                  {/* Progress Bar */}
                  <div className="mb-6" data-id="element-234">
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-2" data-id="element-235">
                      <span data-id="element-236">Your Score</span>
                      <span data-id="element-237">Passing: {passingThreshold}%</span>
                    </div>
                    <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden" data-id="element-238">
                      {/* Passing threshold marker */}
                      <div className="absolute top-0 bottom-0 w-0.5 bg-gray-400 z-10" style={{
                      left: `${passingThreshold}%`
                    }} data-id="element-239" />
                      {/* Score fill */}
                      <motion.div initial={{
                      width: 0
                    }} animate={{
                      width: `${scorePercentage}%`
                    }} transition={{
                      duration: 2,
                      ease: 'easeOut'
                    }} className="h-full bg-gradient-to-r from-[#00638C] to-[#004d6d]" data-id="element-240" />
                    </div>
                  </div>
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4" data-id="element-241">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-242">
                      <div className="w-10 h-10 rounded-full bg-[#00638C]/10 flex items-center justify-center flex-shrink-0" data-id="element-243">
                        <Target size={20} className="text-[#00638C]" data-id="element-244" />
                      </div>
                      <div data-id="element-245">
                        <div className="text-xs text-gray-500" data-id="element-246">
                          Answered Correctly
                        </div>
                        <div className="text-lg font-bold text-gray-900" data-id="element-247">
                          {adjustedCorrectAnswers}/{totalQuestions}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-248">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0" data-id="element-249">
                        <Clock size={20} className="text-green-600" data-id="element-250" />
                      </div>
                      <div data-id="element-251">
                        <div className="text-xs text-gray-500" data-id="element-252">
                          Time to Complete
                        </div>
                        <div className="text-lg font-bold text-gray-900" data-id="element-253">
                          18 min
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Failure Information - Only show if not passed */}
            {!hasPassed && <div className="bg-white rounded-xl p-6 border border-gray-200 mb-6 shadow-sm" data-id="element-254">
                <div className="flex items-start gap-4" data-id="element-255">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#00638C]/10 flex items-center justify-center" data-id="element-256">
                    <AlertTriangle className="text-[#00638C]" size={20} data-id="element-257" />
                  </div>
                  <div className="flex-1" data-id="element-258">
                    <h3 className="font-semibold text-gray-900 text-lg mb-2" data-id="element-259">
                      Next Steps
                    </h3>
                    <p className="text-base text-gray-700 leading-relaxed mb-4" data-id="element-260">
                      Don't worry! Review the detailed feedback below to
                      understand which areas need improvement, then retake the
                      assessment when you're ready.
                    </p>
                    <div className="flex flex-wrap gap-3" data-id="element-261">
                      <button onClick={() => setShowRetakeModal(true)} className="py-2.5 px-5 bg-[#00638C] hover:bg-[#004d6d] text-white rounded-lg font-medium flex items-center transition-colors shadow-sm text-base" data-id="element-262">
                        <RefreshCcw size={16} className="mr-2" data-id="element-263" />
                        Retake Assessment
                      </button>
                      <button onClick={() => setActiveFilter('incorrect')} className="py-2.5 px-5 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 rounded-lg font-medium flex items-center transition-colors text-base" data-id="element-264">
                        <XCircle size={16} className="mr-2" data-id="element-265" />
                        Review Incorrect ({incorrectAnswers})
                      </button>
                    </div>
                  </div>
                </div>
              </div>}
            {/* Filter Controls */}
            <div className="flex items-center justify-between mb-6" data-id="element-266">
              <h2 className="text-lg font-semibold" data-id="element-267">Detailed Feedback</h2>
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1" data-id="element-268">
                <button onClick={() => setActiveFilter('all')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeFilter === 'all' ? 'bg-white shadow-sm' : 'text-gray-600 hover:bg-gray-200'}`} data-id="element-269">
                  All ({totalQuestions})
                </button>
                <button onClick={() => setActiveFilter('correct')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeFilter === 'correct' ? 'bg-white shadow-sm' : 'text-gray-600 hover:bg-gray-200'}`} data-id="element-270">
                  <span className="flex items-center" data-id="element-271">
                    <CheckCircle size={14} className="text-[#007558] mr-1.5" data-id="element-272" />
                    Correct ({adjustedCorrectAnswers})
                  </span>
                </button>
                <button onClick={() => setActiveFilter('incorrect')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeFilter === 'incorrect' ? 'bg-white shadow-sm' : 'text-gray-600 hover:bg-gray-200'}`} data-id="element-273">
                  <span className="flex items-center" data-id="element-274">
                    <XCircle size={14} className="text-red-500 mr-1.5" data-id="element-275" />
                    Incorrect ({incorrectAnswers})
                  </span>
                </button>
              </div>
            </div>
            {/* Question-by-Question Feedback */}
            <div className="space-y-4 mb-8" data-id="element-276">
              {filteredResults.length === 0 ? <div className="p-8 text-center bg-gray-50 rounded-lg border border-gray-200" data-id="element-277">
                  <Filter size={24} className="mx-auto text-gray-400 mb-2" data-id="element-278" />
                  <p className="text-gray-600 text-base" data-id="element-279">
                    No questions match the selected filter.
                  </p>
                </div> : filteredResults.map((result, index) => {
              // Find the original index in the assessmentResults array
              const originalIndex = processedResults.findIndex((r, i) => r === result);
              return <div key={originalIndex} className="border rounded-lg overflow-hidden shadow-sm transition-all hover:shadow-md" data-id="element-280">
                      <div className={`flex items-center justify-between p-4 cursor-pointer ${result.isCorrect ? 'bg-[#F1FBEF]' : 'bg-[#FEF2F2]'}`} onClick={() => toggleQuestionDetails(originalIndex)} data-id="element-281">
                        <div className="flex items-center" data-id="element-282">
                          {result.isCorrect ? <CheckCircle size={20} className="text-[#34A853] mr-3 flex-shrink-0" data-id="element-283" /> : <XCircle size={20} className="text-red-600 mr-3 flex-shrink-0" data-id="element-284" />}
                          <div data-id="element-285">
                            <span className="font-medium text-base" data-id="element-286">
                              Question {originalIndex + 1}
                            </span>
                            <p className="text-sm text-gray-600 mt-1 line-clamp-1" data-id="element-287">
                              {questions[originalIndex].text}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center" data-id="element-288">
                          {result.isCorrect ? <span className="text-xs font-medium bg-[#E6F7EA] text-[#34A853] py-1 px-2 rounded mr-2" data-id="element-289">
                              Correct
                            </span> : <span className="text-xs font-medium bg-red-100 text-red-800 py-1 px-2 rounded mr-2" data-id="element-290">
                              Incorrect
                            </span>}
                          {activeQuestionId === originalIndex ? <ChevronDown size={18} data-id="element-291" /> : <ChevronRight size={18} data-id="element-292" />}
                        </div>
                      </div>
                      {activeQuestionId === originalIndex && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} className="p-4 border-t" data-id="element-293">
                          <div className="mb-4" data-id="element-294">
                            <h4 className="font-medium mb-2 text-base" data-id="element-295">
                              Question:
                            </h4>
                            <p className="text-gray-800 font-medium text-base" data-id="element-296">
                              {questions[originalIndex].question}
                            </p>
                            <p className="text-sm text-gray-600 mt-2" data-id="element-297">
                              {questions[originalIndex].instruction}
                            </p>
                          </div>
                          {questions[originalIndex].type !== 'text' && <div className="mb-4" data-id="element-298">
                              <h4 className="font-medium mb-2 text-base" data-id="element-299">
                                Answer Choices:
                              </h4>
                              <div className="space-y-2" data-id="element-300">
                                {questions[originalIndex].options.map((option, optionIndex) => {
                        const isSelected = userAnswers[originalIndex]?.includes(optionIndex);
                        // Determine styling based on selection
                        let bgColor = 'bg-gray-50';
                        let borderColor = 'border-gray-200';
                        let icon = null;
                        let label = null;
                        if (result.isCorrect) {
                          // If the entire question was correct, highlight user's selection
                          if (isSelected) {
                            bgColor = 'bg-[#F1FBEF]';
                            borderColor = 'border-[#CCEAD3]';
                            icon = <CheckCircle size={16} className="text-[#34A853] mr-2 mt-0.5 flex-shrink-0" data-id="element-301" />;
                            label = <span className="ml-auto text-xs font-medium text-[#34A853] bg-[#E6F7EA] px-2 py-0.5 rounded" data-id="element-302">
                                            Your Answer (Correct)
                                          </span>;
                          }
                        } else {
                          // If the question was incorrect, highlight the user's wrong selection
                          if (isSelected) {
                            bgColor = 'bg-[#FEF2F2]';
                            borderColor = 'border-red-300';
                            icon = <XCircle size={16} className="text-red-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-303" />;
                            label = <span className="ml-auto text-xs font-medium text-red-600 bg-red-100 px-2 py-0.5 rounded whitespace-nowrap" data-id="element-304">
                                            Your Answer (Incorrect)
                                          </span>;
                          }
                        }
                        return <div key={optionIndex} className={`p-3 rounded-lg border ${bgColor} ${borderColor} transition-all`} data-id="element-305">
                                        <div className="flex items-start gap-2" data-id="element-306">
                                          {icon}
                                          <span className="flex-1 text-base leading-relaxed text-gray-900" data-id="element-307">
                                            {option}
                                          </span>
                                          {label}
                                        </div>
                                      </div>;
                      })}
                              </div>
                            </div>}
                          {questions[originalIndex].type === 'text' && <div className="mb-4" data-id="element-308">
                              <h4 className="font-medium mb-2 text-base" data-id="element-309">
                                Your Answer:
                              </h4>
                              <div className="p-3 rounded-lg border border-gray-200 bg-gray-50" data-id="element-310">
                                <p className="text-gray-800 text-base" data-id="element-311">
                                  {userAnswers[originalIndex] || 'No answer provided'}
                                </p>
                              </div>
                            </div>}
                          <div className="mb-4" data-id="element-312">
                            <h4 className="font-medium mb-2 text-base" data-id="element-313">
                              Your Confidence Level:
                            </h4>
                            <div className={`p-4 rounded-lg border ${enhancedConfidenceLevels[originalIndex] === 'sure' ? 'border-[#00638C] bg-[#00638C]/5' : enhancedConfidenceLevels[originalIndex] === 'unsure' ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200 bg-gray-50'}`} data-id="element-314">
                              <div className="flex items-center justify-between" data-id="element-315">
                                <div className="flex items-center" data-id="element-316">
                                  {enhancedConfidenceLevels[originalIndex] === 'sure' ? <>
                                      <div className="w-8 h-8 rounded-full bg-[#00638C]/10 flex items-center justify-center mr-3" data-id="element-317">
                                        <CheckCircle size={16} className="text-[#00638C]" data-id="element-318" />
                                      </div>
                                      <div data-id="element-319">
                                        <p className="font-semibold text-[#00638C] text-base" data-id="element-320">
                                          Sure about answer
                                        </p>
                                        <p className="text-sm text-gray-700" data-id="element-321">
                                          You were confident in your response
                                        </p>
                                      </div>
                                    </> : enhancedConfidenceLevels[originalIndex] === 'unsure' ? <>
                                      <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center mr-3" data-id="element-322">
                                        <AlertTriangle size={16} className="text-yellow-600" data-id="element-323" />
                                      </div>
                                      <div data-id="element-324">
                                        <p className="font-semibold text-yellow-900 text-base" data-id="element-325">
                                          Unsure about answer
                                        </p>
                                        <p className="text-sm text-yellow-700" data-id="element-326">
                                          You were uncertain in your response
                                        </p>
                                      </div>
                                    </> : <>
                                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mr-3" data-id="element-327">
                                        <InfoIcon size={16} className="text-gray-600" data-id="element-328" />
                                      </div>
                                      <div data-id="element-329">
                                        <p className="font-semibold text-gray-900 text-base" data-id="element-330">
                                          No confidence level
                                        </p>
                                        <p className="text-sm text-gray-700" data-id="element-331">
                                          Confidence level not selected
                                        </p>
                                      </div>
                                    </>}
                                </div>
                                {!result.isCorrect && enhancedConfidenceLevels[originalIndex] === 'unsure' && <span className="text-xs font-medium bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full whitespace-nowrap" data-id="element-332">
                                      Good self-awareness
                                    </span>}
                              </div>
                            </div>
                          </div>
                          <div data-id="element-333">
                            <h4 className="font-medium mb-2 text-base" data-id="element-334">
                              Feedback:
                            </h4>
                            <div className="p-3 rounded-lg border border-gray-200 bg-gray-50" data-id="element-335">
                              <p className="text-gray-800 text-base" data-id="element-336">
                                {!result.isCorrect && questions[originalIndex].type === 'multiple-choice' ? 'You got some correct but missed at least one. Review the material and try again.' : result.feedback}
                              </p>
                            </div>
                          </div>
                        </motion.div>}
                    </div>;
            })}
            </div>
            {/* Next Steps - Always shown at bottom */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 mb-6 shadow-sm" data-id="element-337">
              <div className="flex items-start gap-4" data-id="element-338">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#00638C]/10 flex items-center justify-center" data-id="element-339">
                  <AlertTriangle className="text-[#00638C]" size={20} data-id="element-340" />
                </div>
                <div className="flex-1" data-id="element-341">
                  <h3 className="font-semibold text-gray-900 text-lg mb-2" data-id="element-342">
                    Next Steps
                  </h3>
                  <p className="text-base text-gray-700 leading-relaxed mb-4" data-id="element-343">
                    {hasPassed ? 'Great work on passing! You can review the detailed feedback above to reinforce your understanding, or retake the assessment to further improve your score.' : "Don't worry! Review the detailed feedback above to understand which areas need improvement, then retake the assessment when you're ready."}
                  </p>
                  <div className="flex flex-wrap gap-3" data-id="element-344">
                    <button onClick={() => setShowRetakeModal(true)} className="py-2.5 px-5 bg-[#00638C] hover:bg-[#004d6d] text-white rounded-lg font-medium flex items-center transition-colors shadow-sm text-base" data-id="element-345">
                      <RefreshCcw size={16} className="mr-2" data-id="element-346" />
                      Retake Assessment
                    </button>
                    <button onClick={() => setActiveFilter('incorrect')} className="py-2.5 px-5 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 rounded-lg font-medium flex items-center transition-colors text-base" data-id="element-347">
                      <XCircle size={16} className="mr-2" data-id="element-348" />
                      Review Incorrect ({incorrectAnswers})
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Retake Assessment Modal */}
      <AnimatePresence data-id="element-349">
        {showRetakeModal && <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowRetakeModal(false)} data-id="element-350">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-351">
              <div className="flex items-center gap-3 mb-4" data-id="element-352">
                <div className="w-10 h-10 rounded-full bg-[#00638C]/10 flex items-center justify-center" data-id="element-353">
                  <RefreshCcw size={20} className="text-[#00638C]" data-id="element-354" />
                </div>
                <h2 className="text-lg font-semibold" data-id="element-355">Retake Assessment</h2>
              </div>
              <p className="text-gray-600 mb-6 leading-relaxed text-base" data-id="element-356">
                You can retake the entire assessment or focus only on the
                questions you answered incorrectly. How would you like to
                proceed?
              </p>
              <div className="space-y-3" data-id="element-357">
                <button onClick={() => handleRetakeAssessment(true)} className="w-full py-3 px-4 bg-[#00638C] text-white rounded-lg font-medium hover:bg-[#004d6d] transition-colors flex items-center justify-center text-base" data-id="element-358">
                  <XCircle size={18} className="mr-2" data-id="element-359" />
                  Focus on Incorrect Questions ({incorrectAnswers})
                </button>
                <button onClick={() => handleRetakeAssessment(false)} className="w-full py-3 px-4 border border-[#00638C] bg-white text-[#00638C] rounded-lg font-medium hover:bg-[#00638C]/5 transition-colors flex items-center justify-center text-base" data-id="element-360">
                  <RefreshCcw size={18} className="mr-2" data-id="element-361" />
                  Retake Entire Assessment
                </button>
                <button onClick={() => setShowRetakeModal(false)} className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 transition-colors text-base" data-id="element-362">
                  Cancel
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
      {/* Review Materials Modal */}
      <AnimatePresence data-id="element-363">
        {showReviewModal && <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowReviewModal(false)} data-id="element-364">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="w-full max-w-3xl" onClick={e => e.stopPropagation()} data-id="element-365">
              <div className="bg-white rounded-xl overflow-hidden shadow-2xl" data-id="element-366">
                {/* Header with Background Image */}
                <div className="relative h-48 overflow-hidden" data-id="element-367">
                  {/* Background Image */}
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/eHZPRiiA6H6X56eD4BSZRz/Screenshot_2025-09-30_at_10.52.46_AM.png" alt="Leadership banner" className="w-full h-full object-cover brightness-50" data-id="element-368" />
                  {/* Close Button */}
                  <button onClick={e => {
                e.stopPropagation();
                setShowReviewModal(false);
              }} className="absolute top-4 right-4 w-9 h-9 bg-black bg-opacity-50 rounded-full flex items-center justify-center text-white hover:bg-opacity-70 transition-colors z-10" data-id="element-369">
                    <XIcon size={18} data-id="element-370" />
                  </button>
                  {/* Content */}
                  <div className="absolute inset-0 flex flex-col justify-end p-6 text-white" data-id="element-371">
                    <div className="mb-4" data-id="element-372">
                      <span className="inline-block px-3 py-1 bg-white text-gray-800 rounded-full text-sm font-medium" data-id="element-373">
                        Leadership
                      </span>
                    </div>
                    <h2 className="text-2xl font-bold" data-id="element-374">
                      Strategic Leadership Development
                    </h2>
                  </div>
                </div>
                {/* Tabs */}
                <div className="border-b border-gray-200" data-id="element-375">
                  <div className="flex" data-id="element-376">
                    <button className="px-6 py-3 font-medium text-red-600 border-b-2 border-red-600" data-id="element-377">
                      Overview
                    </button>
                    <button className="px-6 py-3 font-medium text-gray-500 hover:text-gray-700 transition-colors" data-id="element-378">
                      Content
                    </button>
                    <button className="px-6 py-3 font-medium text-gray-500 hover:text-gray-700 transition-colors" data-id="element-379">
                      Resources
                    </button>
                  </div>
                </div>
                {/* Content */}
                <div className="p-6" data-id="element-380">
                  {/* Video Preview */}
                  <div className="mb-8 relative rounded-xl overflow-hidden shadow-md" data-id="element-381">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/eHZPRiiA6H6X56eD4BSZRz/Screenshot_2025-09-30_at_10.52.46_AM.png" alt="Course preview" className="w-full h-auto object-cover rounded-xl" data-id="element-382" />
                    <div className="absolute inset-0 flex items-center justify-center" data-id="element-383">
                      <button className="w-16 h-16 bg-red-600 bg-opacity-90 rounded-full flex items-center justify-center text-white hover:bg-opacity-100 transition-colors transform hover:scale-105" data-id="element-384">
                        <PlayIcon size={32} data-id="element-385" />
                      </button>
                    </div>
                    <div className="absolute bottom-4 left-4 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded" data-id="element-386">
                      Preview: 2:45
                    </div>
                  </div>
                  {/* About This Module */}
                  <div className="mb-8" data-id="element-387">
                    <h3 className="text-lg font-semibold mb-4" data-id="element-388">
                      About this module
                    </h3>
                    <p className="text-gray-700 leading-relaxed" data-id="element-389">
                      Develop essential leadership skills to inspire and guide
                      teams through challenges and change while fostering
                      innovation and driving organizational success.
                    </p>
                  </div>
                  {/* What You'll Learn */}
                  <div data-id="element-390">
                    <div className="flex items-center mb-4" data-id="element-391">
                      <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-600 mr-3" data-id="element-392">
                        <CheckCircle size={18} data-id="element-393" />
                      </div>
                      <h3 className="text-lg font-semibold" data-id="element-394">
                        What you'll learn
                      </h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4" data-id="element-395">
                      <div className="flex items-start" data-id="element-396">
                        <span className="text-red-600 mr-2" data-id="element-397">•</span>
                        <p className="text-gray-700" data-id="element-398">
                          Build effective communication strategies for diverse
                          teams
                        </p>
                      </div>
                      <div className="flex items-start" data-id="element-399">
                        <span className="text-red-600 mr-2" data-id="element-400">•</span>
                        <p className="text-gray-700" data-id="element-401">
                          Learn techniques for managing change and overcoming
                          resistance
                        </p>
                      </div>
                      <div className="flex items-start" data-id="element-402">
                        <span className="text-red-600 mr-2" data-id="element-403">•</span>
                        <p className="text-gray-700" data-id="element-404">
                          Develop strategic thinking and decision-making
                          abilities
                        </p>
                      </div>
                      <div className="flex items-start" data-id="element-405">
                        <span className="text-red-600 mr-2" data-id="element-406">•</span>
                        <p className="text-gray-700" data-id="element-407">
                          Create a personal leadership development plan
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Action Buttons */}
                  <div className="mt-8 flex gap-3 justify-end" data-id="element-408">
                    <button onClick={() => setShowReviewModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-409">
                      Close
                    </button>
                    <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors" data-id="element-410">
                      Start Learning
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
      {/* Chat Feature */}
      <AnimatePresence data-id="element-411">
        {isChatOpen && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 20
      }} className="fixed bottom-24 right-6 w-80 h-96 bg-white rounded-xl shadow-xl overflow-hidden flex flex-col z-10" data-id="element-412">
            <div className="p-4 bg-[#30718D] text-white flex justify-between items-center" data-id="element-413">
              <h3 className="font-semibold" data-id="element-414">Ask About Your Results</h3>
              <button onClick={() => setIsChatOpen(false)} className="text-white hover:text-[#E8F9FA]" data-id="element-415">
                <XIcon size={20} data-id="element-416" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-417">
              {messages.map((message, index) => <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-418">
                  <div className={`max-w-[80%] p-3 rounded-lg ${message.type === 'user' ? 'bg-[#30718D] text-white' : 'bg-gray-100'}`} data-id="element-419">
                    {message.content}
                    <div className="text-xs mt-1 opacity-70" data-id="element-420">
                      {message.timestamp.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                    </div>
                  </div>
                </div>)}
            </div>
            <div className="p-4 border-t" data-id="element-421">
              <div className="flex gap-2" data-id="element-422">
                <input type="text" value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder="Ask about your results..." className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-[#30718D]" onKeyDown={e => {
              if (e.key === 'Enter') {
                sendMessage();
              }
            }} data-id="element-423" />
                <button onClick={sendMessage} className="p-2 text-[#30718D] hover:text-[#25576D]" data-id="element-424">
                  <Send size={20} data-id="element-425" />
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
      {/* Chat Button */}
      <button onClick={() => setIsChatOpen(!isChatOpen)} className="fixed bottom-6 right-6 w-14 h-14 bg-[#30718D] rounded-full shadow-lg flex items-center justify-center text-white hover:bg-[#25576D] transition-colors" data-id="element-426">
        <MessageCircle data-id="element-427" />
      </button>
    </div>;
}